import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Gavel, Trophy, Zap, Shield, Users, Star, ChevronRight, Play, Sparkles } from 'lucide-react';
import useGameStore from '../store/gameStore';
import BirdMascot from '../components/BirdMascot';
import BirdAvatar from '../components/BirdAvatar';
import NicknameSetup from '../components/NicknameSetup';
import WelcomeBackModal from '../components/WelcomeBackModal';
import ProfileModal from '../components/ProfileModal';
import AddToHomeScreen from '../components/AddToHomeScreen';
import Footer from '../components/Footer';
import AdSenseAd from '../components/AdSenseAd';
import MonthlyChampions from '../components/MonthlyChampions';
import MostReactedGallery from '../components/MostReactedGallery';

const LandingPage = () => {
  const navigate = useNavigate();
  const { nickname, avatarId, totalScore, gamesPlayed } = useGameStore();
  const [showNicknameSetup, setShowNicknameSetup] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [mugshotStats, setMugshotStats] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);
  const [mostActivePlayer, setMostActivePlayer] = useState(null);
  const [monthlyLeader, setMonthlyLeader] = useState(null);

  const handlePlayClick = () => {
    const storedNickname = localStorage.getItem('jailbirds_nickname');
    
    if (storedNickname) {
      // Returning player - show welcome back
      setShowWelcomeBack(true);
    } else {
      // New player - show nickname setup
      setShowNicknameSetup(true);
    }
  };

  // Fetch all stats on component mount, when page becomes visible, or when navigating to this page
  React.useEffect(() => {
    const fetchAllStats = async () => {
      try {
        const [mugshotRes, globalRes, mostActiveRes, monthlyRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/most-active`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/monthly?limit=1`)
        ]);
        
        if (mugshotRes.ok) {
          const data = await mugshotRes.json();
          setMugshotStats(data);
        }
        
        if (globalRes.ok) {
          const data = await globalRes.json();
          setGlobalStats(data);
        }
        
        if (mostActiveRes.ok) {
          const data = await mostActiveRes.json();
          if (data.success && data.player) {
            setMostActivePlayer(data.player);
          }
        }
        
        if (monthlyRes.ok) {
          const data = await monthlyRes.json();
          if (data.leaderboard && data.leaderboard.length > 0) {
            setMonthlyLeader(data.leaderboard[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    
    fetchAllStats();
    
    // Refetch when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAllStats();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.key]); // Refetch when route changes
  
  // Listen for storage changes and game completion events
  React.useEffect(() => {
    const handleStorageChange = () => {
      // Refetch stats when storage changes
      Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/stats`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/stats`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/most-active`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/monthly?limit=1`)
      ]).then(async ([mugshotRes, globalRes, mostActiveRes, monthlyRes]) => {
        if (mugshotRes.ok) {
          const data = await mugshotRes.json();
          setMugshotStats(data);
        }
        if (globalRes.ok) {
          const data = await globalRes.json();
          setGlobalStats(data);
        }
        if (dailyRes.ok) {
          const data = await dailyRes.json();
          if (data.leaderboard && data.leaderboard.length > 0) {
            setDailyLeader(data.leaderboard[0]);
          }
        }
        if (monthlyRes.ok) {
          const data = await monthlyRes.json();
          if (data.leaderboard && data.leaderboard.length > 0) {
            setMonthlyLeader(data.leaderboard[0]);
          }
        }
      });
    };
    
    const handleGameCompleted = () => {
      // Force refetch when game completes
      handleStorageChange();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('gameCompleted', handleGameCompleted);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('gameCompleted', handleGameCompleted);
    };
  }, []);

  const handleNicknameComplete = (selectedNickname, avatarId) => {
    setShowNicknameSetup(false);
    // Navigate to game after nickname is set
    navigate('/game');
  };

  const stats = [
    { 
      value: globalStats ? globalStats.unique_players : '0', 
      label: 'Active Players',
      type: 'number'
    },
    { 
      value: globalStats ? globalStats.total_games : '0', 
      label: 'Games Played',
      type: 'number'
    },
    {
      player: mostActivePlayer,
      label: 'Most Games Played',
      type: 'most-active'
    },
    {
      player: monthlyLeader,
      label: 'Monthly Leader',
      type: 'player'
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Modals */}
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
      {showNicknameSetup && <NicknameSetup onComplete={handleNicknameComplete} />}
      {showWelcomeBack && <WelcomeBackModal onClose={() => setShowWelcomeBack(false)} />}
      
      {/* Add to Home Screen Prompt */}
      <AddToHomeScreen />
      
      {/* Disclaimer Banner */}
      <div className="bg-amber-500/10 border-b border-amber-500/30 py-2 px-4 text-center">
        <p className="text-xs sm:text-sm text-muted-foreground">
          ⚖️ All individuals are presumed innocent until proven guilty. Charges shown are allegations only.{' '}
          <Link to="/legal" className="text-primary hover:underline font-medium">
            Learn more
          </Link>
        </p>
      </div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            <BirdMascot size={32} color="#FF6B6B" className="sm:w-10 sm:h-10" />
            <h1 className="text-base sm:text-xl font-heading font-bold text-gradient-primary">JailBirds</h1>
          </motion.div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {nickname && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 hover:border-primary/30 transition-all cursor-pointer"
              >
                <BirdAvatar avatarId={avatarId} size={24} />
                <span className="text-sm font-semibold text-primary">{nickname}</span>
              </motion.button>
            )}
            
            {gamesPlayed > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border"
              >
                <Trophy className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium">{totalScore} pts</span>
              </motion.div>
            )}
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/leaderboard')}
              className="hidden sm:inline-flex text-sm"
              size="sm"
            >
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Leaderboard</span>
            </Button>
            
            <Button 
              onClick={handlePlayClick}
              className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-sm sm:text-base px-3 sm:px-4 py-2"
              size="sm"
            >
              <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Play
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-32 pb-12 sm:pb-20 px-3 sm:px-4 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-hero opacity-50" />
        <div className="absolute top-20 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-accent/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs sm:text-sm">
              <Zap className="w-3 h-3 mr-1" />
              The Ultimate Crime Guessing Game
            </Badge>
            
            {/* Hero Birds */}
            <div className="flex justify-center gap-3 sm:gap-6 mb-4 sm:mb-6">
              <BirdMascot size={50} color="#9333EA" className="sm:w-20 sm:h-20" />
              <BirdMascot size={60} color="#FF6B6B" className="sm:w-24 sm:h-24" />
              <BirdMascot size={50} color="#06B6D4" className="sm:w-20 sm:h-20" />
            </div>
            
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-heading font-bold mb-4 sm:mb-6 leading-tight px-2">
              Can You Guess{' '}
              <span className="text-gradient-primary">The Crime</span>{' '}
              From The{' '}
              <span className="text-gradient-accent">Mugshot?</span>
            </h1>
            
            <p className="text-base sm:text-xl md:text-2xl text-muted-foreground mb-4 sm:mb-6 leading-relaxed px-4">
              Test your detective instincts. Match mugshots with their charges. 
              Compete for glory on the global leaderboard.
            </p>
            
            {/* Live Data Badge */}
            {mugshotStats && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 mb-6 sm:mb-10 text-sm"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="font-medium">
                  <span className="text-red-600 font-bold">Live Data:</span> <span className="text-muted-foreground">{mugshotStats.county}, {mugshotStats.state} • {(() => {
                    const [year, month, day] = mugshotStats.date.split('-');
                    return `${month}/${day}/${year}`;
                  })()} • {mugshotStats.total_records} records</span>
                </span>
              </motion.div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
              <Button 
                size="lg"
                onClick={handlePlayClick}
                className="w-full sm:w-auto bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 group"
              >
                <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:scale-110 transition-transform" />
                Start Playing
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/leaderboard')}
                className="w-full sm:w-auto border-primary/30 text-foreground hover:bg-primary/10 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6"
              >
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                View Leaderboard
              </Button>
            </div>
            
            {/* Facebook Group CTA */}
            <motion.a
              href="https://www.facebook.com/share/1HKRWEXsxt/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/25 hover:bg-[#1877F2]/20 hover:border-[#1877F2]/40 transition-all group cursor-pointer"
              data-testid="facebook-group-cta"
            >
              <svg className="w-4 h-4 text-[#1877F2] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm text-[#1877F2] font-medium group-hover:text-[#1565C0] transition-colors">
                Join us for updates & rewards
              </span>
            </motion.a>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  {stat.type === 'player' || stat.type === 'most-active' ? (
                    stat.player ? (
                      <div className="flex flex-col items-center gap-2">
                        <BirdAvatar avatarId={stat.player.avatar_id} size={64} />
                        <div className="font-semibold text-foreground">{stat.player.nickname}</div>
                        <div className="text-2xl font-heading font-bold text-gradient-primary">
                          {stat.type === 'most-active' 
                            ? `${stat.player.games_played.toLocaleString()} games`
                            : stat.player.score.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">{stat.label}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-xl text-muted-foreground mb-2">No data yet</div>
                        <div className="text-sm text-muted-foreground">{stat.label}</div>
                      </div>
                    )
                  ) : (
                    <>
                      <div className="text-3xl md:text-4xl font-heading font-bold text-gradient-primary mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 10 Most Reacted Mugshots */}
      <MostReactedGallery />

      {/* Monthly Champions Section */}
      <MonthlyChampions />

      {/* Cute Scoring Explanation Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
              How to Score Big! <Sparkles className="inline w-8 h-8 text-gold animate-pulse" />
            </h2>
            <p className="text-muted-foreground mb-4">
              Earn points like a pro detective 🕵️
            </p>
            
            {/* Prize Notice */}
            <div className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 max-w-md mx-auto">
              <div className="text-center">
                <div className="text-sm font-semibold text-amber-500 dark:text-amber-400 mb-1">
                  Monthly Contest
                </div>
                <div className="text-base sm:text-lg font-bold text-amber-500 dark:text-amber-400 mb-1">
                  Win a JailBirdsGame.com T-Shirt!
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground space-y-0.5">
                  <div>Most games played</div>
                  <div>Top monthly points</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Game Info Note */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 max-w-2xl mx-auto"
          >
            <p className="text-sm text-muted-foreground">
              🎲 Every game is <span className="font-semibold text-foreground">randomly generated</span> with fresh mugshots! 
              All your scores add up on the leaderboard. 
              <span className="font-bold text-primary"> Most importantly, have fun! 🎉</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Base Points */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 text-center h-full bg-gradient-card border-primary/20 hover:border-primary/40 transition-all">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Base Points</h3>
                <div className="text-3xl font-bold text-gradient-primary mb-3">+100</div>
                <p className="text-sm text-muted-foreground">
                  Every correct guess! Get 15 right and you're looking at <span className="font-semibold text-foreground">1,500 points</span> 💪
                </p>
              </Card>
            </motion.div>

            {/* Speed Bonus */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 text-center h-full bg-gradient-card border-accent/20 hover:border-accent/40 transition-all">
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Lightning Fast!</h3>
                <div className="text-3xl font-bold text-gradient-accent mb-3">+10 to +100</div>
                <p className="text-sm text-muted-foreground">
                  Speedy detectives get extra points! Answer under <span className="font-semibold text-foreground">3 seconds</span> for max bonus ⚡
                </p>
              </Card>
            </motion.div>

            {/* JailBird Bonus */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-6 text-center h-full bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-amber-500/20 border-gold hover:border-gold transition-all relative overflow-hidden">
                {/* Sparkle effects */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                  <Sparkles className="absolute top-2 left-2 w-4 h-4 text-gold/60 animate-pulse" />
                  <Sparkles className="absolute top-4 right-4 w-3 h-3 text-gold/40 animate-pulse" style={{ animationDelay: '0.3s' }} />
                  <Sparkles className="absolute bottom-6 left-6 w-3 h-3 text-gold/50 animate-pulse" style={{ animationDelay: '0.6s' }} />
                </div>
                
                <div className="absolute top-2 right-2">
                  <Badge className="bg-gold/30 text-gold border-gold/50 text-xs font-bold">SPECIAL</Badge>
                </div>
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-gold/50">
                  <Sparkles className="w-8 h-8 text-white animate-pulse" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2 text-gold">JailBird Bonus! ✨</h3>
                <div className="text-3xl font-bold text-gold mb-3">+500</div>
                <p className="text-sm text-muted-foreground">
                  One lucky mugshot is worth <span className="font-bold text-gold">500 extra points</span> - make it count! 🎯
                </p>
              </Card>
            </motion.div>
          </div>

          {/* Perfect Score Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center"
          >
            <Card className="inline-block px-8 py-4 bg-gradient-to-r from-primary/10 via-accent/10 to-gold/10 border-primary/30">
              <p className="text-sm text-muted-foreground mb-1">Perfect Game? 🏆</p>
              <p className="text-4xl md:text-5xl font-heading font-bold text-gradient-primary">
                3,500 Points!
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                (1,500 base + 1,500 speed + 500 bonus = detective legend status 😎)
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-6 px-4">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <AdSenseAd adSlot="landing-banner" placement="homepage" className="rounded-2xl" />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Start Playing <span className="text-gradient-primary">Now</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Test your detective skills with real mugshots and compete on the leaderboard!
            </p>
            <Button 
              size="lg"
              onClick={handlePlayClick}
              className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold text-lg px-10 py-6 group"
            >
              <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
              Play JailBirds
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Reset Button (if logged in) */}
      {nickname && (
        <div className="py-6 px-4 text-center border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                localStorage.removeItem('jailbirds_nickname');
                localStorage.removeItem('jailbirds_player_id');
                localStorage.removeItem('jailbirds_avatar_id');
                window.location.href = window.location.origin;
              } catch (error) {
                console.error('Reset error:', error);
                alert('Error resetting. Please refresh the page manually.');
              }
            }}
            className="text-xs border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            Reset Nickname & Avatar
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Click to clear your nickname and choose a new one
          </p>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
